import { useState, useCallback, useEffect, useRef } from 'react'
import {
  Lightbulb,
  Wrench,
  Building2,
  GraduationCap,
  Landmark,
  Send,
  Plus,
  History,
  Save,
  Download,
  Copy,
  Check,
  Bot,
  Sparkles,
  FileText,
  ChevronRight,
  Globe,
  BookOpen,
  X,
  Mail,
  PenLine,
  Wand2,
} from 'lucide-react'
import { KaijuLoader } from '../components/KaijuLoader'
import { KbSaveFromSolutionModal } from '../components/solutions/KbSaveFromSolutionModal'
import { QaEngine } from '../components/solutions/QaEngine'
import { EditorWorkspace } from '../components/editor/EditorWorkspace'
import { DocumentStudio } from '../components/studio/DocumentStudio'
import ReactMarkdown from 'react-markdown'
import rehypeHighlight from 'rehype-highlight'
import { useDoctrineMode } from '../hooks/useDoctrineMode'
import { LiteModeNotice } from '../components/LiteModeNotice'

const API_BASE = import.meta.env.VITE_API_BASE_URL || ''

type Mode = 'theory' | 'implementation'
type OrgType = 'federal' | 'state' | 'university' | null

interface Message {
  id?: string
  role: 'user' | 'assistant'
  content: string
  toolCalls?: { name: string }[]
}

interface Session {
  id: string
  title: string | null
  message_count: number
  last_message_at: string | null
  created_at: string
}

interface Template {
  id: string
  name: string
  slug: string
  description: string
  prompt_template: string
  category: string
}

interface SavedSolution {
  id: string
  title: string
  summary?: string
  org_type?: string
  mode?: string
  created_at: string
}

const MODE_OPTIONS = [
  { value: 'theory' as Mode, label: 'Theory', icon: Lightbulb, description: 'Conceptual, strategic focus' },
  { value: 'implementation' as Mode, label: 'Implementation', icon: Wrench, description: 'Technical, detailed specs' },
]

const ORG_OPTIONS = [
  { value: null, label: 'Any', icon: Globe, description: 'General solutions' },
  { value: 'federal' as OrgType, label: 'Federal', icon: Landmark, description: 'DoD/Civilian agencies' },
  { value: 'state' as OrgType, label: 'State', icon: Building2, description: 'State government' },
  { value: 'university' as OrgType, label: 'University', icon: GraduationCap, description: 'Higher education' },
]

function toolPillStyle(name: string) {
  switch (name) {
    case 'gitlab_feature_search':
      return { bg: 'bg-purple-50 text-purple-600', icon: Sparkles, label: 'Feature Search' }
    case 'gitlab_use_case_match':
      return { bg: 'bg-green-50 text-green-600', icon: FileText, label: 'Use Case Match' }
    case 'knowledge_search':
      return { bg: 'bg-blue-50 text-blue-600', icon: BookOpen, label: 'Knowledge Base' }
    case 'web_search':
      return { bg: 'bg-orange-50 text-orange-600', icon: Globe, label: 'Web Search' }
    default:
      return { bg: 'bg-gray-100 text-gray-600', icon: null, label: name }
  }
}

type TopMode = 'builder' | 'qa' | 'editor' | 'studio'

export function SolutionsBuilderPage() {
  const { isLite, isFeatureAvailable } = useDoctrineMode()
  if (isLite && !isFeatureAvailable('solutions-kb')) {
    return <LiteModeNotice feature="Solutions Builder" />
  }
  return <SolutionsBuilderPageInner />
}

function SolutionsBuilderPageInner() {
  // Top-level mode toggle
  const [topMode, setTopMode] = useState<TopMode>('builder')

  // Mode and context
  const [mode, setMode] = useState<Mode>('theory')
  const [orgType, setOrgType] = useState<OrgType>(null)

  // Session state
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [sessions, setSessions] = useState<Session[]>([])
  const [messages, setMessages] = useState<Message[]>([])

  // UI state
  const [streamingMessage, setStreamingMessage] = useState<string | null>(null)
  const [toolStatus, setToolStatus] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [showSidebar, setShowSidebar] = useState(true)
  const [sidebarTab, setSidebarTab] = useState<'templates' | 'history' | 'saved'>('templates')
  const [copiedMessageIndex, setCopiedMessageIndex] = useState<number | null>(null)
  const [copiedHeader, setCopiedHeader] = useState(false)
  const [kbSaveContent, setKbSaveContent] = useState<string | null>(null)

  // Templates and saved solutions
  const [templates, setTemplates] = useState<Template[]>([])
  const [savedSolutions, setSavedSolutions] = useState<SavedSolution[]>([])

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streamingMessage])

  // Load templates on mount
  useEffect(() => {
    loadTemplates()
    loadSessions()
    loadSavedSolutions()
  }, [])

  const loadTemplates = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/solutions/templates`)
      if (res.ok) {
        const data = await res.json()
        setTemplates(data.templates || [])
      }
    } catch (err) {
      console.error('Failed to load templates:', err)
    }
  }

  const loadSessions = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/chat/sessions?scope=solutions-builder`)
      if (res.ok) {
        const data = await res.json()
        setSessions(data.data?.sessions || [])
      }
    } catch (err) {
      console.error('Failed to load sessions:', err)
    }
  }

  const loadSavedSolutions = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/solutions?limit=20`)
      if (res.ok) {
        const data = await res.json()
        setSavedSolutions(data.solutions || [])
      }
    } catch (err) {
      console.error('Failed to load saved solutions:', err)
    }
  }

  const loadMessages = async (sid: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/chat/sessions/${sid}/messages`)
      if (res.ok) {
        const data = await res.json()
        setMessages(data.data?.messages || [])
      }
    } catch (err) {
      console.error('Failed to load messages:', err)
    }
  }

  const createSession = async (): Promise<string> => {
    const res = await fetch(`${API_BASE}/api/chat/sessions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        scope: 'solutions-builder',
        entityType: orgType || undefined,
        useMemory: true,
      }),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    const session = data.data?.session
    setSessionId(session.id)
    return session.id
  }

  const handleSend = useCallback(async (message?: string) => {
    const text = message || inputValue.trim()
    if (!text) return

    setIsLoading(true)
    setStreamingMessage('')
    setToolStatus(null)
    setInputValue('')

    // Add user message immediately
    setMessages(prev => [...prev, { role: 'user', content: text }])

    try {
      let sid = sessionId
      if (!sid) {
        sid = await createSession()
      }

      const res = await fetch(`${API_BASE}/api/chat/sessions/${sid}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          scope: 'solutions-builder',
          entityType: orgType || undefined,
          metadata: { mode, orgType },
          useMemory: true,
        }),
      })

      if (!res.ok) throw new Error(`Server returned ${res.status}`)

      const reader = res.body?.getReader()
      const decoder = new TextDecoder()
      let accumulatedText = ''
      let toolCallsUsed: { name: string }[] = []

      if (reader) {
        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          const chunk = decoder.decode(value, { stream: true })
          const lines = chunk.split('\n')

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            const payload = line.slice(6).trim()
            if (payload === '[DONE]') continue

            try {
              const event = JSON.parse(payload)

              if (event.type === 'text') {
                accumulatedText += event.text
                setStreamingMessage(accumulatedText)
              } else if (event.type === 'tool_status') {
                if (event.status === 'executing') {
                  setToolStatus(event.tool)
                } else {
                  setToolStatus(null)
                  toolCallsUsed.push({ name: event.tool })
                }
              } else if (event.type === 'error') {
                accumulatedText += `\n\n[Error: ${event.message}]`
                setStreamingMessage(accumulatedText)
              }
            } catch {
              // Skip unparseable lines
            }
          }
        }
      }

      setStreamingMessage(null)
      setToolStatus(null)
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: accumulatedText,
        toolCalls: toolCallsUsed.length > 0 ? toolCallsUsed : undefined,
      }])

      loadSessions()

    } catch (err: any) {
      setStreamingMessage(null)
      setToolStatus(null)
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Error: ${err.message || 'Failed to get response'}`,
      }])
    } finally {
      setIsLoading(false)
    }
  }, [inputValue, sessionId, mode, orgType])

  const handleNewChat = useCallback(() => {
    setSessionId(null)
    setMessages([])
    setStreamingMessage(null)
    inputRef.current?.focus()
  }, [])

  const handleSelectSession = useCallback((session: Session) => {
    setSessionId(session.id)
    loadMessages(session.id)
    setSidebarTab('templates')
  }, [])

  const handleTemplateClick = useCallback((template: Template) => {
    const prompt = template.prompt_template
      .replace('{org_type}', orgType || 'SLED')
      .replace('{requirements}', '[describe specific requirements]')
      .replace('{compliance_framework}', '[FedRAMP/NIST/CMMC/StateRAMP]')
      .replace('{source_platform}', '[current platform]')
      .replace('{project_count}', '[number]')
    setInputValue(prompt)
    inputRef.current?.focus()
  }, [orgType])

  const handleSaveSolution = useCallback(async () => {
    if (messages.length === 0) return

    const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant')
    if (!lastAssistant) return

    const title = prompt('Enter a title for this solution:')
    if (!title) return

    try {
      await fetch(`${API_BASE}/api/solutions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          title,
          content: lastAssistant.content,
          orgType,
          mode,
        }),
      })
      loadSavedSolutions()
      alert('Solution saved!')
    } catch (err) {
      console.error('Failed to save solution:', err)
    }
  }, [messages, sessionId, orgType, mode])

  const handleExportChat = useCallback(() => {
    if (messages.length === 0) return

    let markdown = `# Solutions Builder Export\n\n`
    markdown += `**Date:** ${new Date().toISOString().split('T')[0]}\n`
    markdown += `**Mode:** ${mode}\n`
    if (orgType) markdown += `**Organization Type:** ${orgType}\n`
    markdown += '\n---\n\n'

    for (const msg of messages) {
      const label = msg.role === 'user' ? '## Question' : '## Answer'
      markdown += `${label}\n\n${msg.content}\n\n---\n\n`
    }

    const blob = new Blob([markdown], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `solutions-export-${new Date().toISOString().split('T')[0]}.md`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [messages, mode, orgType])

  const handleCopyLast = useCallback(async () => {
    const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant')
    if (!lastAssistant) return

    await navigator.clipboard.writeText(lastAssistant.content)
    setCopiedHeader(true)
    setTimeout(() => setCopiedHeader(false), 2000)
  }, [messages])

  const handleCopyMessage = useCallback(async (content: string, index: number) => {
    try {
      await navigator.clipboard.writeText(content)
      setCopiedMessageIndex(index)
      setTimeout(() => setCopiedMessageIndex(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }, [])

  const handleDownloadMessage = useCallback((content: string) => {
    const blob = new Blob([content], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const date = new Date().toISOString().split('T')[0]
    a.download = `solution-response-${date}.md`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className="h-[calc(100vh-140px)] flex flex-col">
      {/* Top-level mode toggle: Builder | Q&A Engine */}
      <div className="flex items-center gap-1 px-4 py-2 border-b border-gray-200 dark:border-white/10 bg-white/80 dark:bg-surface-900/80 flex-shrink-0">
        <button
          onClick={() => setTopMode('builder')}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
            topMode === 'builder'
              ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-400'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-white/10'
          }`}
        >
          <Bot className="w-3.5 h-3.5" />
          Builder
        </button>
        <button
          onClick={() => setTopMode('qa')}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
            topMode === 'qa'
              ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-400'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-white/10'
          }`}
        >
          <Mail className="w-3.5 h-3.5" />
          Q&A Engine
        </button>
        <button
          onClick={() => setTopMode('editor')}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
            topMode === 'editor'
              ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-400'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-white/10'
          }`}
        >
          <PenLine className="w-3.5 h-3.5" />
          Editor
        </button>
        <button
          onClick={() => setTopMode('studio')}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
            topMode === 'studio'
              ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-400'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-white/10'
          }`}
        >
          <Wand2 className="w-3.5 h-3.5" />
          Studio
        </button>
      </div>

      {/* Q&A Engine view */}
      {topMode === 'qa' ? (
        <div className="flex-1 min-h-0 p-4">
          <QaEngine />
        </div>
      ) : topMode === 'editor' ? (
        <div className="flex-1 min-h-0">
          <EditorWorkspace />
        </div>
      ) : topMode === 'studio' ? (
        <div className="flex-1 min-h-0">
          <DocumentStudio />
        </div>
      ) : (
      <div className="flex-1 min-h-0 flex">
      {/* Sidebar */}
      {showSidebar && (
        <div className="w-72 border-r border-gray-200 flex flex-col bg-gray-50">
          {/* Sidebar tabs */}
          <div className="flex border-b border-gray-200 bg-white">
            <button
              onClick={() => setSidebarTab('templates')}
              className={`flex-1 px-3 py-2.5 text-xs font-medium transition-colors ${
                sidebarTab === 'templates'
                  ? 'text-primary-600 border-b-2 border-primary-500 bg-primary-50/50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Templates
            </button>
            <button
              onClick={() => setSidebarTab('history')}
              className={`flex-1 px-3 py-2.5 text-xs font-medium transition-colors ${
                sidebarTab === 'history'
                  ? 'text-primary-600 border-b-2 border-primary-500 bg-primary-50/50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              History
            </button>
            <button
              onClick={() => setSidebarTab('saved')}
              className={`flex-1 px-3 py-2.5 text-xs font-medium transition-colors ${
                sidebarTab === 'saved'
                  ? 'text-primary-600 border-b-2 border-primary-500 bg-primary-50/50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Saved
            </button>
          </div>

          {/* Sidebar content */}
          <div className="flex-1 overflow-y-auto p-3">
            {sidebarTab === 'templates' && (
              <div className="space-y-2">
                {templates.map(t => (
                  <button
                    key={t.id}
                    onClick={() => handleTemplateClick(t)}
                    className="w-full text-left p-3 bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-sm transition-all group"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">{t.name}</span>
                      <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-primary-500 transition-colors" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{t.description}</p>
                  </button>
                ))}
              </div>
            )}

            {sidebarTab === 'history' && (
              <div className="space-y-2">
                <button
                  onClick={handleNewChat}
                  className="w-full flex items-center gap-2 p-2.5 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  New Chat
                </button>
                {sessions.map(s => (
                  <button
                    key={s.id}
                    onClick={() => handleSelectSession(s)}
                    className={`w-full text-left p-2.5 rounded-lg border transition-all ${
                      sessionId === s.id
                        ? 'bg-primary-50 border-primary-200'
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {s.title || 'Untitled Session'}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {s.message_count} messages
                    </div>
                  </button>
                ))}
              </div>
            )}

            {sidebarTab === 'saved' && (
              <div className="space-y-2">
                {savedSolutions.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No saved solutions yet</p>
                ) : (
                  savedSolutions.map(s => (
                    <div
                      key={s.id}
                      className="p-3 bg-white rounded-lg border border-gray-200"
                    >
                      <div className="text-sm font-medium text-gray-900">{s.title}</div>
                      <div className="flex items-center gap-2 mt-1">
                        {s.org_type && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                            {s.org_type}
                          </span>
                        )}
                        {s.mode && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-primary-50 text-primary-600 rounded">
                            {s.mode}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Knowledge Base link */}
          <div className="border-t border-gray-200 p-3">
            <a
              href="/solutions/knowledge-base"
              className="flex items-center gap-2 p-2.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium"
            >
              <BookOpen className="w-4 h-4" />
              Knowledge Base
              <ChevronRight className="w-4 h-4 ml-auto text-gray-400" />
            </a>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="border-b border-gray-200 bg-white px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Toggle sidebar */}
              <button
                onClick={() => setShowSidebar(!showSidebar)}
                className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 transition-colors"
              >
                {showSidebar ? <X className="w-4 h-4" /> : <History className="w-4 h-4" />}
              </button>

              <div className="flex items-center gap-2">
                <Bot className="w-5 h-5 text-primary-500" />
                <h1 className="text-base font-semibold text-gray-900">Solutions Builder</h1>
              </div>

              {/* Mode toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                {MODE_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setMode(opt.value)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      mode === opt.value
                        ? 'bg-white text-primary-600 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <opt.icon className="w-3.5 h-3.5" />
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Org type selector */}
              <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                {ORG_OPTIONS.map(opt => (
                  <button
                    key={opt.value || 'any'}
                    onClick={() => setOrgType(opt.value)}
                    title={opt.description}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
                      orgType === opt.value
                        ? 'bg-white text-primary-600 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <opt.icon className="w-3.5 h-3.5" />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1">
              <button
                onClick={handleCopyLast}
                disabled={messages.length === 0}
                className="p-2 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Copy last response"
              >
                {copiedHeader ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </button>
              <button
                onClick={handleSaveSolution}
                disabled={messages.length === 0}
                className="p-2 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Save solution"
              >
                <Save className="w-4 h-4" />
              </button>
              <button
                onClick={handleExportChat}
                disabled={messages.length === 0}
                className="p-2 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Export chat"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {messages.length === 0 && !streamingMessage ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 bg-primary-50 rounded-2xl flex items-center justify-center mb-4">
                <Lightbulb className="w-8 h-8 text-primary-400" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">GitLab Solutions Builder</h2>
              <p className="text-sm text-gray-500 max-w-md mb-6">
                Design GitLab solutions for customer questions and business problems.
                Start with theory, then dive into implementation details.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {templates.slice(0, 4).map(t => (
                  <button
                    key={t.id}
                    onClick={() => handleTemplateClick(t)}
                    className="px-4 py-2 bg-primary-50 hover:bg-primary-100 text-primary-700 rounded-full text-sm font-medium transition-colors"
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4 max-w-4xl mx-auto">
              {messages.map((msg, i) => (
                <div key={i}>
                  {msg.role === 'user' ? (
                    <div className="flex justify-end">
                      <div className="max-w-[80%] bg-primary-600 text-white rounded-2xl rounded-br-md px-4 py-2.5 text-sm">
                        {msg.content}
                      </div>
                    </div>
                  ) : (
                    <div>
                      {/* Tool pills */}
                      {msg.toolCalls && msg.toolCalls.length > 0 && (
                        <div className="flex items-center gap-2 mb-2 ml-10">
                          {msg.toolCalls.map((tc, ti) => {
                            const style = toolPillStyle(tc.name)
                            return (
                              <div
                                key={ti}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${style.bg}`}
                              >
                                {style.icon && <style.icon className="w-3 h-3" />}
                                {style.label}
                              </div>
                            )
                          })}
                        </div>
                      )}
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-7 h-7 bg-primary-50 rounded-lg flex items-center justify-center mt-0.5">
                          <Bot className="w-4 h-4 text-primary-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="prose prose-sm max-w-none prose-headings:text-gray-900 prose-p:text-gray-700">
                            <ReactMarkdown rehypePlugins={[rehypeHighlight]}>
                              {msg.content}
                            </ReactMarkdown>
                          </div>
                          {/* Action buttons */}
                          <div className="flex items-center gap-1 mt-2 pt-2 border-t border-gray-100">
                            <button
                              onClick={() => handleCopyMessage(msg.content, i)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                              title="Copy to clipboard"
                            >
                              {copiedMessageIndex === i ? (
                                <>
                                  <Check className="w-3.5 h-3.5 text-green-500" />
                                  <span className="text-green-600">Copied</span>
                                </>
                              ) : (
                                <>
                                  <Copy className="w-3.5 h-3.5" />
                                  <span>Copy</span>
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => handleDownloadMessage(msg.content)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                              title="Download as markdown"
                            >
                              <Download className="w-3.5 h-3.5" />
                              <span>Download</span>
                            </button>
                            <button
                              onClick={() => setKbSaveContent(msg.content)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-indigo-700 hover:bg-indigo-50 rounded transition-colors"
                              title="Save to Knowledge Base"
                            >
                              <BookOpen className="w-3.5 h-3.5" />
                              <span>Save to KB</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Tool status */}
              {toolStatus && (
                <div className="flex items-center gap-2 ml-10">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-600 rounded-full text-[11px] font-medium">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                    {toolPillStyle(toolStatus).label}...
                  </div>
                </div>
              )}

              {/* Streaming message */}
              {streamingMessage && (
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-7 h-7 bg-primary-50 rounded-lg flex items-center justify-center mt-0.5">
                    <Bot className="w-4 h-4 text-primary-500" />
                  </div>
                  <div className="flex-1 min-w-0 prose prose-sm max-w-none">
                    <ReactMarkdown rehypePlugins={[rehypeHighlight]}>
                      {streamingMessage}
                    </ReactMarkdown>
                    <span className="inline-block w-0.5 h-4 bg-primary-500 animate-pulse ml-0.5" />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input area */}
        <div className="border-t border-gray-200 bg-white p-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-end gap-3">
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    mode === 'theory'
                      ? 'Ask about GitLab solutions, compliance, or architecture...'
                      : 'Ask for implementation details, configs, or step-by-step guidance...'
                  }
                  className="w-full resize-none rounded-xl border border-gray-300 px-4 py-3 pr-12 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none transition-colors"
                  rows={1}
                  style={{ minHeight: '48px', maxHeight: '120px' }}
                />
              </div>
              <button
                onClick={() => handleSend()}
                disabled={isLoading || !inputValue.trim()}
                className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary-600 text-white flex items-center justify-center hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <KaijuLoader size="sm" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-gray-400">
                {mode === 'theory' ? 'Theory mode: Strategic focus' : 'Implementation mode: Technical details'}
                {orgType && ` | ${orgType.charAt(0).toUpperCase() + orgType.slice(1)}`}
              </p>
              <p className="text-xs text-gray-400">
                Press Enter to send, Shift+Enter for new line
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Save to KB Modal */}
      {kbSaveContent && (
        <KbSaveFromSolutionModal
          content={kbSaveContent}
          sessionId={sessionId}
          onClose={() => setKbSaveContent(null)}
          onSaved={() => setKbSaveContent(null)}
        />
      )}
    </div>
      )}
    </div>
  )
}
